import { lineFontSize } from '../state';

import { buildDefaultMulti } from './buildDefaultMulti';
import { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';
import type { Multi, MultiDependencies } from './Multi';
import {
  buildStretchDelimiter,
  isStretchDelimiterChar,
  StretchDelimiter,
} from './StretchDelimiter';
import { buildText, Text } from './Text';

const GROUP_RATIO = 1.25;
const MIN_VERTICAL_PADDING = 2;
const TARGET_HEIGHT_RATIO = 1.1;
const OPENING_DELIMITER_LEADING_PADDING = 2;
const SIMPLE_INLINE_BAR_PADDING = 1.5;
const SIMPLE_INLINE_BRACE_PADDING = 1.5;

// Ratio for how closely to squash nested objects (0-1, smaller is closer)
const NESTED_CONTENT_RATIO = 0.75;
const SAME_DELIMITER_LEFT_START_RATIO = 0.375;
const SAME_DELIMITER_RIGHT_OVERLAP_RATIO = 0.625;
const MIXED_STRETCH_LEFT_START_RATIO = 0.55;
const MIXED_STRETCH_RIGHT_OVERLAP_RATIO = 0.28;
const TEXT_MIXED_LEFT_START_RATIO = 0.72;
const TEXT_MIXED_RIGHT_OVERLAP_RATIO = 0.12;

type NestedBoundaryDensity = 'sameDelimiter' | 'mixedStretch' | 'textMixed';
type BoundarySide = 'left' | 'right';

export class Quantity extends MathObject {
  multi: Multi;
  line: Line;
  vertToBaseline = 0;

  leftParen: MathObject;
  rightParen: MathObject;

  constructor(line: Line, multi: Multi, lChar: MathObject, rChar: MathObject) {
    super();

    this.t = 'quantity';
    this.gmmName = 'quantity';

    this.multi = multi;
    this.line = line;

    this.leftParen = lChar;
    this.rightParen = rChar;

    this.children.push(this.leftParen);
    // Text delimiters intentionally stay parentless here because resizing them
    // triggers childSizeChanged() and would recurse back into Quantity.buildSize().
    if (!(this.leftParen instanceof Text)) this.leftParen.parent = this;
    this.children.push(this.multi);
    this.multi.parent = this;
    this.children.push(this.rightParen);
    if (!(this.rightParen instanceof Text)) this.rightParen.parent = this;
  }

  getPaintedCharCount(): number {
    // '+2' to consider enclosing graphic at each side of the quantity as one char
    return this.multi.getPaintedCharCount() + 2;
  }

  toXML(): string {
    return '<q>' + this.multi.toXML() + '</q>';
  }

  swapString(old: string, replacer: string): void {
    this.multi.swapString(old, replacer);
  }

  setFont(f: string): void {
    this.multi.setFont(f);
  }

  setEditable(b: boolean): void {
    this.multi.setEditable(b);
  }

  buildSizeRecursive(): void {
    this.multi.buildSizeRecursive();
    this.buildSize();
  }

  hasLeftBoundQuantity(): boolean {
    return this.getLeftBoundQuantity() instanceof Quantity;
  }

  hasRightBoundQuantity(): boolean {
    return this.getRightBoundQuantity() instanceof Quantity;
  }

  private getLeftBoundQuantity(): Quantity | undefined {
    const child = this.children[1];
    const first = child.children[0] as Text;

    return first instanceof Text &&
      first.text === '' &&
      child.children[1] instanceof Quantity
      ? (child.children[1] as Quantity)
      : undefined;
  }

  private getRightBoundQuantity(): Quantity | undefined {
    const child = this.children[this.children.length - 2];
    const last = child.children[child.children.length - 1] as Text;

    return last instanceof Text &&
      last.text === '' &&
      child.children[child.children.length - 2] instanceof Quantity
      ? (child.children[child.children.length - 2] as Quantity)
      : undefined;
  }

  setLine(line: Line): void {
    this.line = line;
    this.multi.setLine(line);
  }

  grabFocus(direction: Direction): void {
    this.multi.grabFocus(direction);
  }

  buildSize(): void {
    const leadingPadding = this.getLeadingPadding();
    const leftBoundQuantity = this.getLeftBoundQuantity();
    const rightBoundQuantity = this.getRightBoundQuantity();
    const textDelimiterFontSize = this.multi.viewportH * GROUP_RATIO;
    const verticalPadding = this.getVerticalPadding();
    const targetHeight = this.getTargetDelimiterHeight(
      textDelimiterFontSize,
      verticalPadding
    );

    // Stretch delimiters are sized from the content box first, then synced to
    // the final quantity height so they can still line up with legacy Text
    // fallbacks like braces. The target baseline keeps enough of the legacy
    // centered-glyph envelope that grouped fractions do not make following
    // inline text ride too high.
    this.vertToBaseline = this.getTargetVertToBaseline(
      targetHeight,
      verticalPadding
    );
    this.resizeDelimiter(
      this.leftParen,
      targetHeight,
      this.vertToBaseline,
      textDelimiterFontSize
    );
    this.resizeDelimiter(
      this.rightParen,
      targetHeight,
      this.vertToBaseline,
      textDelimiterFontSize
    );
    this.vertToBaseline = Math.max(
      this.vertToBaseline,
      this.leftParen.getVertToBaseline(),
      this.rightParen.getVertToBaseline()
    );
    const finalHeight = Math.max(
      targetHeight,
      this.multi.viewportH,
      this.leftParen.viewportH,
      this.rightParen.viewportH
    );

    this.syncStretchDelimiterMetrics(finalHeight);
    this.setPositionsOfChildren(
      leftBoundQuantity,
      rightBoundQuantity,
      leadingPadding
    );
    const rightmostEdge = Math.max(
      this.leftParen.viewportX + this.leftParen.viewportW,
      this.multi.viewportX + this.multi.viewportW,
      this.rightParen.viewportX + this.rightParen.viewportW
    );

    this.setAllDim(rightmostEdge, finalHeight);
  }

  protected getVerticalPadding(): number {
    // Most grouped content just needs a small cushion above and below the
    // interior expression so the delimiters do not look cramped as they grow.
    const extraHeight = Math.max(
      this.multi.viewportH * TARGET_HEIGHT_RATIO - this.multi.viewportH,
      MIN_VERTICAL_PADDING * 2
    );

    return Math.ceil(extraHeight / 2);
  }

  protected getTargetDelimiterHeight(
    textDelimiterFontSize: number,
    verticalPadding: number
  ): number {
    // Stretch delimiters should keep the older text-delimiter envelope even
    // though we now draw them ourselves. That preserves the familiar vertical
    // feel around grouped fractions while still using the lighter custom art.
    // Legacy Text delimiters effectively floored the font size via parseInt,
    // so mirror that integer height here instead of keeping fractional pixels.
    return Math.max(
      this.multi.viewportH + verticalPadding * 2,
      Math.floor(textDelimiterFontSize)
    );
  }

  protected getTargetVertToBaseline(
    targetHeight: number,
    verticalPadding: number
  ): number {
    return Math.max(
      targetHeight / 2,
      this.multi.getVertToBaseline() + verticalPadding
    );
  }

  getVertToBaseline(): number {
    return this.vertToBaseline;
  }

  setPositionsOfChildren(
    leftBoundQuantity: Quantity | undefined = this.getLeftBoundQuantity(),
    rightBoundQuantity: Quantity | undefined = this.getRightBoundQuantity(),
    leadingPadding = this.getLeadingPadding()
  ): void {
    this.leftParen.viewportX = leadingPadding;
    this.multi.viewportX =
      leadingPadding + this.getLeftContentStart(leftBoundQuantity);
    this.multi.viewportY =
      this.getVertToBaseline() - this.multi.getVertToBaseline();

    this.rightParen.viewportX =
      this.multi.viewportX +
      this.multi.viewportW -
      this.getRightOverlapWidth(rightBoundQuantity);
  }

  childSizeChanged(): void {
    this.buildSize();
    this.getValueParent()?.childSizeChanged();
  }

  add(addMe: MathObject): void {
    this.multi.add(addMe);
  }

  private resizeDelimiter(
    delimiter: MathObject,
    targetHeight: number,
    vertToBaseline: number,
    textDelimiterFontSize: number
  ): void {
    if (delimiter instanceof StretchDelimiter) {
      delimiter.setMetrics(targetHeight, vertToBaseline);

      return;
    }

    if (delimiter instanceof Text) {
      delimiter.setFontSize(textDelimiterFontSize);
    }
  }

  private syncStretchDelimiterMetrics(targetHeight: number): void {
    if (this.leftParen instanceof StretchDelimiter) {
      this.leftParen.setMetrics(targetHeight, this.vertToBaseline);
    }

    if (this.rightParen instanceof StretchDelimiter) {
      this.rightParen.setMetrics(targetHeight, this.vertToBaseline);
    }
  }

  private getLeadingPadding(): number {
    return this.leftParen instanceof StretchDelimiter &&
      (this.leftParen.char === '(' || this.leftParen.char === '[')
      ? OPENING_DELIMITER_LEADING_PADDING
      : 0;
  }

  private getLeftContentStart(boundQuantity: Quantity | undefined): number {
    if (!boundQuantity) {
      return (
        this.leftParen.viewportW * NESTED_CONTENT_RATIO +
        this.getBoundaryInlineDelimiterPadding(this.leftParen, 'left')
      );
    }

    const ratio = this.getNestedBoundaryRatios(
      this.leftParen,
      boundQuantity.leftParen
    ).leftStartRatio;

    return this.leftParen.viewportW * ratio;
  }

  private getRightOverlapWidth(boundQuantity: Quantity | undefined): number {
    if (!boundQuantity) {
      return (
        this.rightParen.viewportW -
        this.rightParen.viewportW * NESTED_CONTENT_RATIO -
        this.getBoundaryInlineDelimiterPadding(this.rightParen, 'right')
      );
    }

    const ratio = this.getNestedBoundaryRatios(
      this.rightParen,
      boundQuantity.rightParen
    ).rightOverlapRatio;

    return this.rightParen.viewportW * ratio;
  }

  private getNestedBoundaryRatios(
    outerDelimiter: MathObject,
    innerDelimiter: MathObject
  ): {
    leftStartRatio: number;
    rightOverlapRatio: number;
  } {
    // Same-symbol nesting can overlap more aggressively than mixed shapes,
    // while text fallbacks need a little more breathing room.
    const density = this.getNestedBoundaryDensity(
      outerDelimiter,
      innerDelimiter
    );

    if (density === 'sameDelimiter') {
      return {
        leftStartRatio: SAME_DELIMITER_LEFT_START_RATIO,
        rightOverlapRatio: SAME_DELIMITER_RIGHT_OVERLAP_RATIO,
      };
    }

    if (density === 'textMixed') {
      return {
        leftStartRatio: TEXT_MIXED_LEFT_START_RATIO,
        rightOverlapRatio: TEXT_MIXED_RIGHT_OVERLAP_RATIO,
      };
    }

    return {
      leftStartRatio: MIXED_STRETCH_LEFT_START_RATIO,
      rightOverlapRatio: MIXED_STRETCH_RIGHT_OVERLAP_RATIO,
    };
  }

  private getNestedBoundaryDensity(
    outerDelimiter: MathObject,
    innerDelimiter: MathObject
  ): NestedBoundaryDensity {
    const outerChar = this.getDelimiterChar(outerDelimiter);
    const innerChar = this.getDelimiterChar(innerDelimiter);

    if (
      outerChar !== undefined &&
      innerChar !== undefined &&
      outerChar === innerChar
    ) {
      return 'sameDelimiter';
    }

    if (
      !(outerDelimiter instanceof StretchDelimiter) ||
      !(innerDelimiter instanceof StretchDelimiter)
    ) {
      return 'textMixed';
    }

    return 'mixedStretch';
  }

  private getBoundaryInlineDelimiterPadding(
    delimiter: MathObject,
    side: BoundarySide
  ): number {
    // Fractions already create their own interior whitespace. The follow-up
    // tweak is only for cases where the edge-adjacent child is still an inline
    // text-like object, even if the rest of the quantity contains a fraction.
    if (!this.hasTextLikeBoundaryChild(side)) {
      return 0;
    }

    const delimiterChar = this.getDelimiterChar(delimiter);

    if (delimiterChar === '|') {
      return SIMPLE_INLINE_BAR_PADDING;
    }

    if (delimiterChar === '{' || delimiterChar === '}') {
      return SIMPLE_INLINE_BRACE_PADDING;
    }

    return 0;
  }

  private hasTextLikeBoundaryChild(side: BoundarySide): boolean {
    const boundaryChild = this.getBoundaryChild(this.multi, side);

    return boundaryChild
      ? this.isTextLikeBoundaryChild(boundaryChild, side)
      : false;
  }

  private getBoundaryChild(
    multi: Multi,
    side: BoundarySide
  ): MathObject | undefined {
    const meaningfulChildren = multi
      .getValueChildren()
      .filter(child => !(child instanceof Text && child.text.length === 0));

    return side === 'left'
      ? meaningfulChildren[0]
      : meaningfulChildren[meaningfulChildren.length - 1];
  }

  private isTextLikeBoundaryChild(
    child: MathObject,
    side: BoundarySide
  ): boolean {
    if (child instanceof Text) {
      return child.text.trim().length > 0;
    }

    if (child.t === 'power') {
      const powerBranch = child.getValueChildren()[side === 'left' ? 0 : 1] as
        | Multi
        | undefined;

      if (!powerBranch) {
        return false;
      }

      const powerBoundaryChild = this.getBoundaryChild(powerBranch, side);

      return powerBoundaryChild
        ? this.isTextLikeBoundaryChild(powerBoundaryChild, side)
        : false;
    }

    return false;
  }

  private getDelimiterChar(delimiter: MathObject): string | undefined {
    if (delimiter instanceof StretchDelimiter) {
      return delimiter.char;
    }

    if (delimiter instanceof Text && delimiter.text.length === 1) {
      return delimiter.text;
    }

    return undefined;
  }
}

// Keep the parser/editor paths simple: they still hand Quantity Text
// delimiters, and Quantity upgrades the supported one-character cases here.
const maybeBuildStretchDelimiter = (delimiter: Text): MathObject =>
  delimiter.text.length === 1 && isStretchDelimiterChar(delimiter.text)
    ? buildStretchDelimiter(delimiter.text)
    : delimiter;

// Default delimiters are the hot path, so build the canvas-rendered version
// directly instead of first creating a Text node that would be thrown away.
const buildDefaultDelimiter = (
  problemJS: MultiDependencies,
  delimiterText: string
): MathObject =>
  isStretchDelimiterChar(delimiterText)
    ? buildStretchDelimiter(delimiterText)
    : buildText(problemJS, 0, 0, delimiterText, lineFontSize * GROUP_RATIO);

export function buildQuantity(
  problemJS: MultiDependencies,
  line: Line,
  contents?: Multi,
  useAbValIfNoLCharRChar?: boolean,
  lChar?: Text,
  rChar?: Text
): Quantity {
  const defaultLeftChar = useAbValIfNoLCharRChar ? '|' : '(';
  const defaultRightChar = useAbValIfNoLCharRChar ? '|' : ')';

  return new Quantity(
    line,
    contents || buildDefaultMulti(problemJS, line),
    lChar
      ? maybeBuildStretchDelimiter(lChar)
      : buildDefaultDelimiter(problemJS, defaultLeftChar),
    rChar
      ? maybeBuildStretchDelimiter(rChar)
      : buildDefaultDelimiter(problemJS, defaultRightChar)
  );
}
